import { Link, useParams } from 'react-router';
import type { MonitoringAlert } from '@bekebi/contracts/api';
import { ArrowLeftIcon } from '../../components/icons';
import { ErrorState, LoadingState } from '../../components/states';
import { isServiceId, useService } from '../services/api';
import { DefinitionList, HealthBadge, Section, ServiceTypeIcon } from '../services/components';
import {
  CHECK_KIND_TITLES,
  HEALTH_REASON_TEXT,
  formatDateTime,
  formatRelative,
} from '../services/format';
import {
  ALERT_REASON_TEXT,
  ALERT_RESOLUTION_TEXT,
  AlertStateBadge,
  formatDuration,
} from './alert-present';
import { useAlert, useAlertNotifications, useChecks } from './api';
import { DeliveryRow } from './notifications-section';
import { CheckStateBadge } from './present';

const linkClass =
  'text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link';

/**
 * One alert (/app/monitoring/alerts/:alertId, Admin, `monitoring.view`): what the alert recorded,
 * the service's health now, and the checks behind it — all read from the API. Alerts are opened
 * and resolved by the monitoring worker; this page has nothing to change.
 */
export function AlertDetail() {
  const { alertId } = useParams();
  if (!isServiceId(alertId)) return <AlertNotFound />;
  return <AlertDetailFor id={alertId} />;
}

function AlertNotFound() {
  return (
    <div className="max-w-3xl">
      <h1>Alert not found</h1>
      <p className="mt-2 text-sm text-muted">There is no alert at this address.</p>
      <p className="mt-4 text-sm">
        <Link to="/app/monitoring/alerts" className={linkClass}>
          Back to Alerts
        </Link>
      </p>
    </div>
  );
}

function AlertDetailFor({ id }: { id: string }) {
  const alert = useAlert(id);
  if (alert.isPending) return <LoadingState label="Loading alert…" />;
  if (alert.isError) {
    return (
      <ErrorState
        title="The alert could not be loaded"
        message={alert.error.message}
        onRetry={() => void alert.refetch()}
      />
    );
  }
  if (!alert.data) return <AlertNotFound />;
  return <AlertView alert={alert.data} />;
}

function AlertView({ alert }: { alert: MonitoringAlert }) {
  const open = alert.state === 'open';
  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <Link
          to="/app/monitoring/alerts"
          className="inline-flex items-center gap-1.5 text-sm text-accent-link hover:underline"
        >
          <ArrowLeftIcon className="size-4" />
          Alerts
        </Link>
        <div className="mt-3 flex items-start gap-3">
          <ServiceTypeIcon type={alert.service.type} size="lg" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <h1>{alert.service.name} alert</h1>
              <AlertStateBadge alert={alert} />
            </div>
            <p className="mt-1 text-sm text-muted">
              {open
                ? `Opened ${formatRelative(alert.openedAt)}. It resolves automatically when the service’s checks pass again.`
                : `Lasted ${formatDuration(alert.openedAt, alert.resolvedAt!)}.`}
            </p>
          </div>
        </div>
      </div>

      <Section title="Alert">
        <DefinitionList
          items={[
            [
              'Service',
              <Link key="s" to={`/app/services/${alert.service.id}`} className={linkClass}>
                {alert.service.name}
              </Link>,
            ],
            ['Reason', ALERT_REASON_TEXT[alert.reason]],
            ['Opened', formatDateTime(alert.openedAt)],
            [
              open ? 'Open for' : 'Resolved',
              open
                ? formatDuration(alert.openedAt, new Date().toISOString())
                : formatDateTime(alert.resolvedAt!),
            ],
            ...(alert.resolution
              ? ([['Outcome', ALERT_RESOLUTION_TEXT[alert.resolution]]] as [string, string][])
              : []),
          ]}
        />
        <p className="mt-5 text-xs text-muted">
          Alerts are opened and resolved by the monitoring worker from the service&rsquo;s check
          results. They can&rsquo;t be changed by hand.
        </p>
      </Section>

      <ServiceNow serviceId={alert.service.id} alertOpen={open} />
      <AlertEmails alertId={alert.id} />
      <ServiceChecks serviceId={alert.service.id} />
    </div>
  );
}

/** The service's health now, as the API derives it — it may have changed since the alert. */
function ServiceNow({ serviceId, alertOpen }: { serviceId: string; alertOpen: boolean }) {
  const service = useService(serviceId);
  return (
    <Section title="Service now">
      {service.isPending ? (
        <LoadingState label="Loading the service’s current health…" />
      ) : service.isError ? (
        <ErrorState
          title="The service’s current health could not be loaded"
          message={service.error.message}
          onRetry={() => void service.refetch()}
        />
      ) : service.data ? (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
            <HealthBadge health={service.data.health} />
            <span className="text-fg-2">{HEALTH_REASON_TEXT[service.data.health.reason]}</span>
          </div>
          {alertOpen && service.data.health.state === 'unknown' ? (
            // Unknown is not recovered: the incident stays open, pending a meaningful result.
            <p className="mt-3 rounded-lg border border-line bg-canvas/40 px-3 py-2 text-sm text-fg-2">
              No current check result yet. This alert stays open until the service&rsquo;s checks
              pass again.
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-muted">This service could not be found.</p>
      )}
    </Section>
  );
}

/** The service's checks and their latest results (technical layer, Admin). */
function ServiceChecks({ serviceId }: { serviceId: string }) {
  const checks = useChecks(serviceId);
  const items = checks.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Section title="Checks on this service">
      {checks.isPending ? (
        <LoadingState label="Loading checks…" />
      ) : checks.isError ? (
        <ErrorState
          title="Checks could not be loaded"
          message={checks.error.message}
          onRetry={() => void checks.refetch()}
        />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted">This service has no checks.</p>
      ) : (
        <ul aria-label="Checks on this service" className="divide-y divide-line">
          {items.map((check) => (
            <li
              key={check.id}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3 text-sm first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <Link to={`/app/monitoring/${check.id}`} className={`font-semibold ${linkClass}`}>
                  {CHECK_KIND_TITLES[check.kind]}
                </Link>
                <p className="mt-0.5 text-xs text-muted">
                  {check.lastCheckedAt
                    ? `Last checked ${formatDateTime(check.lastCheckedAt)}`
                    : 'No result yet'}
                </p>
              </div>
              <CheckStateBadge check={check} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/**
 * This alert's alert and recovery emails (Phase 07 Stage 05-C): each recipient by role and Bekebi
 * User ID, whether the mail server accepted the email, and any failure — never an address.
 */
function AlertEmails({ alertId }: { alertId: string }) {
  const notifications = useAlertNotifications(alertId);
  const items = notifications.data?.items ?? [];
  return (
    <Section title="Alert emails">
      {notifications.isPending ? (
        <LoadingState label="Loading this alert’s emails…" />
      ) : notifications.isError ? (
        <ErrorState
          title="This alert’s emails could not be loaded"
          message={notifications.error.message}
          onRetry={() => void notifications.refetch()}
        />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted">
          {notifications.data.emailSetUp
            ? 'No emails for this alert. An alert email is sent once an alert has been open for 10 minutes.'
            : 'No emails for this alert. Alert emails are not set up on the server.'}
        </p>
      ) : (
        <>
          <ul aria-label="Emails for this alert" className="divide-y divide-line">
            {items.map((delivery) => (
              <li key={delivery.id} className="py-3 first:pt-0 last:pb-0">
                <DeliveryRow delivery={delivery} />
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted">
            &ldquo;Accepted by the mail server&rdquo; means the mail server took the email; it does
            not confirm that it reached the inbox.
          </p>
        </>
      )}
    </Section>
  );
}
